import PropTypes from 'prop-types';
import { useRef } from 'react';
import cls from 'classnames';

import useSize from '@/utils/hooks/useSize';

import { analytics, getClickCtaDetail } from '@/utils/analytics';
import { isDDEnv, isWHEnv, getContent } from '@/utils/helper';

import Pill from '@/atoms/Pill/Pill';
import CtfCta from '@/molecules/CTA/index';
import { DynamicAsset } from '@/molecules/Media/index';
import RichText from '@/molecules/RichText/RichText';

import {
  CtfCtaStyle,
  CtfAssetPositions,
  CtfWidthOptions,
} from '@/utils/constants';
import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';
import Text from '@/atoms/Text/Text';
import MapWestholme from '../Map/MapWestholme';

function StoryNormal({ assets = [], compactSpace, entries = [], fields = {} }) {
  const storyRef = useRef(null);
  const size = useSize(storyRef);
  // @TODO: Improve variable spreading and type checking.
  const {
    id,
    title,
    text,
    callToAction,
    superText,
    headingWeight,
    dynamicAsset,
    assetPosition,
    width,
    hasBackground,
    hasRoundedAsset,
    hideText,
    hideHeader,
    cloudinaryAssets,
    mobileCloudinaryAsset,
    removeYPadding,
    tag: contentTag,
    subText,
    locationText,
    mapButton,
    backgroundColor
  } = fields;
  const HeadingTag = headingWeight ? headingWeight.toLowerCase() : 'h3';
  const altCase = width === CtfWidthOptions.FULL_WIDTH && hasBackground;
  const tag = contentTag?.sys?.id
    ? getContent(contentTag?.sys?.id, entries)
    : null;
  const mobileAsset = mobileCloudinaryAsset ?? cloudinaryAssets;
  const isDD = isDDEnv();
  const isWH = isWHEnv();
  const contentMap = getContent(mapButton?.sys?.id, entries);

  return (
    <div
      className={cls('aaco-story aaco-story-normal', {
        'mb-20': !compactSpace && !removeYPadding && isDD,
        '': !compactSpace && !removeYPadding && isWH,
        [`bg-${backgroundColor}`]: backgroundColor !== undefined,
      })}
      id={id}
    >
      <div
        className={cls(
          'flex items-center',
          'h-full min-h-fit',
          {
            'flex-col-reverse md:flex-row justify-between px-6 py-9 md:p-10': isWH,
            'ml-0 pl-0': width === CtfWidthOptions.NO_LEFT && isWH,
            'mr-0 pr-0': width === CtfWidthOptions.NO_RIGHT && isWH,
            'lg:container lg:mx-auto xl:max-w-full 2xl:max-w-screen-2xl 2xl:gap-10':
              width !== CtfWidthOptions.FULL_WIDTH && !compactSpace,

            'ml-0 pl-0': width === CtfWidthOptions.NO_LEFT && isDD,
            'mr-0 pr-0': width === CtfWidthOptions.NO_RIGHT && isDD,
          },
          {
            'bg-progressive-blue lg:mr-[15em] mt-20':
              altCase && !compactSpace && isDD,
            'lg:bg-light-warm-grey lg:mt-[60px] min-h-[580px]':
              altCase && !compactSpace && isWH,
          },
          ` ${
            hasBackground
              ? 'dd-en:bg-progressive-blue dd-kr:bg-progressive-blue'
              : ''
          } ${
            width === CtfWidthOptions.PADDING &&
            hasBackground &&
            'dd-en:rounded-[8px] dd-kr:mx-[30px]'
          }`
        )}
        ref={(dynamicAsset || cloudinaryAssets) && storyRef}
      >
        <div
          className={cls(
            'aaco-story__image aaco-story__image--is-mobile',
            'lg:hidden w-full',
            'mb-0',
            {
              'absolute top-0 right-0': altCase && !compactSpace && isWH,
              'translate-x-[30px]': isWH && hasBackground,
              '!h-full !w-full': isWH,
            }
          )}
        >
          {(mobileAsset || dynamicAsset) && (
            <DynamicAsset
              assets={assets}
              callToAction={callToAction}
              controls={false}
              entries={entries}
              field={mobileAsset ?? dynamicAsset}
              size={size}
              lightbox
              container={
                width !== CtfWidthOptions.PADDING ? 'half' : 'portrait'
              }
              style={cls({
                'aspect-[4/5]': isWH,
                'rounded-none lg:rounded-[8px]':
                  isDD &&
                  hasRoundedAsset &&
                  width === CtfWidthOptions.FULL_WIDTH,
                'rounded-[8px]':
                  isDD &&
                  hasRoundedAsset &&
                  width !== CtfWidthOptions.FULL_WIDTH,
              })}
            />
          )}
        </div>
        <div
          className={cls(
            'aaco-story__image aaco-story__image--is-desktop',
            {
              'lg:w-1/2': !compactSpace,
              'lg:relative lg:top-[-5em] lg:right-[-8em] lg:max-w-[40%]':
                altCase && !compactSpace && isDD,
              'lg:absolute lg:top-[-60px] lg:bottom-[60px] lg:right-0 lg:w-[690px] lg:max-w-[60%]':
                altCase && !compactSpace && isWH,
            },
            'hidden lg:block',
            'mb-8 dd-kr:mb-0 dd-en:mb-0 lg:mb-0',
            {
              'translate-x-[90px]': isWH && hasBackground,
              '!h-full !max-w-[490px] !w-full': isWH,
            },

            {
              'lg:order-1':
                assetPosition === CtfAssetPositions.LEFT && !compactSpace,
              'lg:order-3':
                assetPosition !== CtfAssetPositions.LEFT && !compactSpace,
            }
          )}
        >
          {(cloudinaryAssets || dynamicAsset) && (
            <DynamicAsset
              assets={assets}
              callToAction={callToAction}
              controls={false}
              entries={entries}
              field={cloudinaryAssets ?? dynamicAsset}
              size={size}
              lightbox
              container={
                width !== CtfWidthOptions.PADDING ? 'half' : 'portrait'
              }
              style={`aspect-[4/5] ${
                hasRoundedAsset
                  ? width === CtfWidthOptions.FULL_WIDTH
                    ? 'dd-en:rounded-none dd-kr:rounded-none lg:dd-en:rounded-[8px] lg:dd-kr:rounded-[8px]'
                    : 'dd-en:rounded-[8px] dd-kr:rounded-[8px]'
                  : ''
              }`}
            />
          )}
        </div>
        {!hideText && (
          <div
            className={cls(
              'aaco-story__content',
              `min-h-full order-2
            ${
              hasBackground
                ? 'bg-light-warm-grey text-white self-stretch dd-kr:bg-progressive-blue dd-en:bg-progressive-blue'
                : 'text-grey-6 self-center dd-kr:pb-0 dd-en:pb-0'
            } ${
                !compactSpace &&
                altCase &&
                'lg:flex lg:flex-col lg:justify-center lg:alt 2xl:ml-[]'
              }`,
              {
                'w-full': isWH,
                'lg:w-[calc(100vw-780px)] ': isWH && !compactSpace,
                '2xl:dd-kr:max-w-half': isDD,
              },
              'p-8',
              {
                'lg:pl-16': !compactSpace,
                'pt-[25px] lg:py-8 px-[30px] lg:px-[100px]': isDD,
                '!p-0 !self-start': isWH,
              }
            )}
          >
            {superText && (
              <h6
                className={`mb-4 text-tag uppercase ${
                  hasBackground
                    ? 'text-white dd-en:80-white-on-dark-blue dd-kr:80-white-on-dark-blue'
                    : 'text- dd-en:text-grey-6 dd-kr:text-grey-6'
                } ${!compactSpace && !isDDEnv() && 'lg:ml-1/3'} ${isWH && '!m-0 !text-black font-pro-mono'}
              `}
              >
                {superText}
              </h6>
            )}
            {tag && (
              <Pill
                style="pill-action mb-6 inline-flex items-center"
                onClick={() => router.push(`/search?t=${tag?.fields?.slug}`)}
                small
              >
                {tag.fields?.name}
              </Pill>
            )}
            {title && !hideHeader && (
              <>
                <HeadingTag
                  className={`mb-6 ${
                    isDDEnv() ? TEXT_BASE_CLASS.h4 : TEXT_BASE_CLASS.h3
                  } ${
                    hasBackground
                      ? 'text-black dd-en:text-white dd-kr:text-white'
                      : 'text-black dd-en:text-progressive-blue dd-kr:text-progressive-blue'
                  } ${
                    !compactSpace &&
                    'mb-[22px] lg:dd-kr:mb-[15px] lg:dd-en:mb-[15px]'
                  }`}
                >
                  {title}
                </HeadingTag>
                <hr className="border-black my-8" />
              </>
            )}
            {subText && (
              <>
                <Text
                  tag="h5"
                  style="text-black w-full md:w-[366px]"
                >
                  {subText}
                </Text>
                <hr className="border-black my-8" />
              </>
            )}
            <div
              className={`aaco-story__inner-content flex flex-col md:flex-row ${!isDDEnv() && 'ml-1/3'} ${isWH && '!m-0 py-6 flex-col-reverse md:gap-0 gap-3'}`}
            >
              <div className={cls(
                "text-content-story-normal flex-1 grid gap-y-4", {
                  "md:w-[385px]": isWH,
                }
              )}>
                {text && !text.content && (
                  <p
                    className={`text-body ${
                      hasBackground
                        ? 'dd-en:80-white-on-dark-blue dd-kr:80-white-on-dark-blue'
                        : 'dd-en:text-grey-6 dd-kr:text-grey-6'
                    } ${!isDDEnv() && 'text-eucalyptus'}`}
                  >
                    {text}
                  </p>
                )}
                {text &&
                  text.content &&
                  text.content.length > 0 &&
                  text.content.map(({ nodeType, content }, index) => (
                    <RichText
                      entries={entries}
                      nodeType={nodeType}
                      content={content}
                      key={index}
                      richTextStyle={{
                        paragraph: {
                          customFont:
                            TEXT_BASE_CLASS['body'] +
                            (hasBackground
                              ? ' text-black dd-en:text-80-white-on-dark-blue dd-kr:text-80-white-on-dark-blue'
                              : ' dd-en:text-grey-6 dd-kr:text-grey-6') +
                            ' font-text',
                          blockquoteFont: TEXT_BASE_CLASS['blockquote'],
                        },
                      }}
                    />
                  ))}
              </div>
              {locationText && (
                <div className="text-location-story-normal text-left md:text-right flex-1">
                  <Text
                    tag="tag"
                    style="text-transparent border-l-1 border-l-transparent pl-4 font-pro-mono"
                  >
                    {locationText}
                  </Text>
                </div>
              )}
            </div>
            {contentMap && (
              <MapWestholme
                fields={contentMap.fields}
                assets={assets}
                entries={entries}
              />
            )}
            {callToAction && (
                <div
                  className={`aaco-story__cta pt-3 dd-kr:lg:pt-5 dd-en:lg:pt-5 ${
                    hasBackground ? 'text-white' : 'text-black'
                  }`}
                >
                  <CtfCta
                    type={
                      isDDEnv() ? CtfCtaStyle.DD_SECONDARY : CtfCtaStyle.PRIMARY
                    }
                    className="text-acacia"
                    field={callToAction}
                    entries={entries}
                    invert={isDDEnv() && hasBackground}
                    onClick={(e, { localUrl, url, name }) =>
                      analytics({
                        window,
                        document,
                        detail: getClickCtaDetail(e, { localUrl, url, name }),
                      })
                    }
                  />
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
}

// @TODO: define real props for the Story based on it requirements
StoryNormal.propTypes = {
  assets: PropTypes.array,
  compactSpace: PropTypes.bool,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

export default StoryNormal;
